import React, { useState, useEffect } from "react";
import { Table, Typography, Spin, Tag, Tooltip, Empty } from "antd";
import { ClockCircleOutlined, WarningOutlined, CheckCircleOutlined, HistoryOutlined, InfoCircleOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { getFlightTimeVersions } from "../../../../api/flightOperationApi";
import FlightTimeDisplay from "../../../../components/DateTimeDisplay";

const { Text } = Typography;

/**
 * 航班时间版本历史组件
 * 以简洁表格形式展示每次计划发布时的STD/STA
 *
 * @param {number} flightId - 航班ID
 * @param {boolean} visible - 是否可见（用于触发加载）
 * @param {Object} flight - 航班数据（用于获取机场代码）
 * @param {Object} airportsMap - 机场信息映射，用于时区转换
 */
const FlightChangesSummary = ({ flightId, visible, flight, airportsMap = {} }) => {
    const [loading, setLoading] = useState(false);
    const [versions, setVersions] = useState([]);
    const [error, setError] = useState(null);

    // 获取时间版本历史
    useEffect(() => {
        if (!visible || !flightId) {
            return;
        }

        const fetchVersions = async () => {
            setLoading(true);
            setError(null);

            try {
                const response = await getFlightTimeVersions(flightId);
                if (response?.data?.success) {
                    setVersions(response.data.data || []);
                } else {
                    setError("获取历史数据失败");
                }
            } catch (err) {
                console.error("获取航班时间版本历史失败:", err);
                if (err.response?.status === 404) {
                    setVersions([]);
                } else {
                    setError(err.response?.data?.detail || "获取历史数据失败");
                }
            } finally {
                setLoading(false);
            }
        };

        fetchVersions();
    }, [visible, flightId]);

    // 格式化发布时间
    const formatPublishTime = (time) => {
        if (!time) return "-";
        return dayjs(time).format("MM-DD HH:mm");
    };

    // 格式化时间差
    const formatTimeDiff = (minutes) => {
        if (minutes === null || minutes === undefined || minutes === 0) return null;

        const absMinutes = Math.abs(minutes);
        const hours = Math.floor(absMinutes / 60);
        const mins = absMinutes % 60;

        let result = "";
        if (hours > 0) {
            result += `${hours}h`;
            if (mins > 0) result += `${mins}m`;
        } else {
            result = `${mins}m`;
        }

        return minutes > 0 ? `+${result}` : `-${result}`;
    };

    // 获取时间差的Tag颜色
    const getDiffTagColor = (minutes) => {
        if (minutes === null || minutes === undefined || minutes === 0) return null;
        if (minutes > 0) {
            // 延误
            if (minutes >= 120) return "error";
            if (minutes >= 60) return "warning";
            return "gold";
        } else {
            // 提前
            return "success";
        }
    };

    // 渲染时间差Tag
    const renderTimeDiff = (minutes) => {
        const diffText = formatTimeDiff(minutes);
        if (!diffText) return null;

        const color = getDiffTagColor(minutes);
        return (
            <Tag color={color} style={{ fontSize: "10px", padding: "0 3px", margin: 0, lineHeight: "14px" }}>
                {diffText}
            </Tag>
        );
    };

    // 跳转到航班计划页面
    const handleGoToSchedule = (scheduleId) => {
        if (scheduleId) {
            // 跳转到航班计划列表页面，并带上计划ID参数
            window.open(`/flightSchedule/list?scheduleId=${scheduleId}`, "_blank");
        }
    };

    // 表格列定义
    const columns = [
        {
            title: "版本",
            dataIndex: "version",
            key: "version",
            width: 50,
            align: "center",
            render: (v, record) => {
                const hasSchedule = record.source_schedule_id;
                return (
                    <span>
                        {hasSchedule ? (
                            <Tooltip title="点击查看来源计划">
                                <a onClick={() => handleGoToSchedule(record.source_schedule_id)} style={{ fontSize: "11px", fontWeight: record.is_current ? "bold" : "normal" }}>
                                    V{v}
                                </a>
                            </Tooltip>
                        ) : (
                            <Text style={{ fontSize: "11px", fontWeight: record.is_current ? "bold" : "normal" }}>V{v}</Text>
                        )}
                        {record.is_current && (
                            <Tag color="blue" style={{ fontSize: "9px", marginLeft: 2, padding: "0 2px" }}>
                                当前
                            </Tag>
                        )}
                    </span>
                );
            },
        },
        {
            title: "发布时间",
            dataIndex: "created_at",
            key: "created_at",
            width: 75,
            align: "center",
            render: (time) => <Text style={{ fontSize: "10px", color: "#666" }}>{formatPublishTime(time)}</Text>,
        },
        {
            title: "STD",
            dataIndex: "std",
            key: "std",
            width: 65,
            align: "center",
            render: (time, record) => (
                <span>
                    <FlightTimeDisplay utcTime={time} airportCode={flight?.departure_airport} airportsMap={airportsMap} format="HH:mm" style={{ fontSize: "11px" }} />
                    {record.std_diff_minutes !== null && record.version > 1 && <span style={{ marginLeft: 2 }}>{renderTimeDiff(record.std_diff_minutes)}</span>}
                </span>
            ),
        },
        // {
        //     title: "STA",
        //     dataIndex: "sta",
        //     key: "sta",
        //     width: 65,
        //     align: "center",
        //     render: (time, record) => (
        //         <span>
        //             <Text style={{ fontSize: "11px" }}>{formatTime(time)}</Text>
        //             {record.sta_diff_minutes !== null && record.version > 1 && (
        //                 <span style={{ marginLeft: 2 }}>{renderTimeDiff(record.sta_diff_minutes)}</span>
        //             )}
        //         </span>
        //     ),
        // },
        {
            title: "ETD",
            dataIndex: "etd",
            key: "etd",
            width: 65,
            align: "center",
            render: (time, record) => {
                if (!time) return <Text style={{ fontSize: "10px", color: "#ccc" }}>-</Text>;
                return (
                    <span>
                        <FlightTimeDisplay utcTime={time} airportCode={flight?.departure_airport} airportsMap={airportsMap} format="HH:mm" stdTime={record.std} style={{ fontSize: "11px", color: "#fa8c16" }} />
                        {record.etd_diff_minutes !== null && record.version > 1 && <span style={{ marginLeft: 2 }}>{renderTimeDiff(record.etd_diff_minutes)}</span>}
                    </span>
                );
            },
        },
        // {
        //     title: "ETA",
        //     dataIndex: "eta",
        //     key: "eta",
        //     width: 65,
        //     align: "center",
        //     render: (time, record) => {
        //         if (!time) return <Text style={{ fontSize: "10px", color: "#ccc" }}>-</Text>;
        //         return (
        //             <span>
        //                 <Text style={{ fontSize: "11px", color: "#fa8c16" }}>{formatTime(time)}</Text>
        //                 {record.eta_diff_minutes !== null && record.version > 1 && (
        //                     <span style={{ marginLeft: 2 }}>{renderTimeDiff(record.eta_diff_minutes)}</span>
        //                 )}
        //             </span>
        //         );
        //     },
        // },
    ];

    // 无数据或只有一个版本时不显示
    if (!loading && versions.length <= 1) {
        return null;
    }

    // 计算总变更（第一个版本到最后一个版本）
    const renderTotalChange = () => {
        if (versions.length < 2) return null;

        const first = versions[0];
        const last = versions[versions.length - 1];

        if (!first.std || !last.std) return null;

        const totalStdDiff = dayjs(last.std).diff(dayjs(first.std), "minute");
        const totalStaDiff = first.sta && last.sta ? dayjs(last.sta).diff(dayjs(first.sta), "minute") : null;

        if (totalStdDiff === 0 && (totalStaDiff === null || totalStaDiff === 0)) return null;

        const diffText = formatTimeDiff(totalStdDiff);
        const isSignificant = Math.abs(totalStdDiff) >= 120;

        return (
            <div style={{ marginTop: 6, padding: "4px 8px", background: isSignificant ? "#fff2e8" : "#f6ffed", borderRadius: 4, fontSize: "11px" }}>
                {isSignificant ? <WarningOutlined style={{ color: "#fa541c", marginRight: 4 }} /> : <InfoCircleOutlined style={{ color: "#52c41a", marginRight: 4 }} />}
                <Text style={{ fontSize: "11px" }}>
                    累计变更: STD {diffText}
                    {totalStaDiff !== null && totalStaDiff !== 0 && `, STA ${formatTimeDiff(totalStaDiff)}`}
                </Text>
                {isSignificant && <Text style={{ fontSize: "10px", color: "#fa541c", marginLeft: 8 }}>建议核查航权</Text>}
            </div>
        );
    };

    return (
        <div style={{ marginTop: 8 }}>
            <div style={{ marginBottom: 4, display: "flex", alignItems: "center" }}>
                <HistoryOutlined style={{ color: "#1890ff", marginRight: 4 }} />
                <Text style={{ fontSize: "12px", fontWeight: 500 }}>时间版本历史</Text>
                <Tooltip title="显示每次计划发布时的STD/STA，便于对比时间变更">
                    <InfoCircleOutlined style={{ fontSize: "10px", color: "#999", marginLeft: 4, cursor: "help" }} />
                </Tooltip>
            </div>

            {loading ? (
                <div style={{ textAlign: "center", padding: "12px" }}>
                    <Spin size="small" />
                </div>
            ) : error ? (
                <div style={{ color: "#ff4d4f", fontSize: "11px", padding: "8px" }}>{error}</div>
            ) : (
                <>
                    <Table dataSource={versions} columns={columns} rowKey="flight_id" size="small" pagination={false} showHeader={true} style={{ fontSize: "11px" }} rowClassName={(record) => (record.is_current ? "current-version-row" : "")} />
                    {renderTotalChange()}
                </>
            )}

            <style>{`
                .current-version-row {
                    background-color: #e6f7ff !important;
                }
            `}</style>
        </div>
    );
};

export default FlightChangesSummary;
